<template>
  <section class="hero is-default is-bold">
    <div class="hero-body">
      <div class="container">
        <div class="columns">
          <div class="column is-one-quarter">
            <b-field v-if="authStore.isAuthenticated && uuid">
              <div class="control is-expanded is-clearfix">
                <b-dropdown :expanded="true" aria-role="list">
                  <template #trigger="{ active }">
                    <b-button
                      expanded
                      :label="buttonLabel"
                      type="is-primary"
                      :icon-left="buttonIcon"
                      :icon-right="active ? 'caret-up' : 'caret-down'"
                    />
                  </template>
                  <b-dropdown-item
                    v-for="scenario in adminStore.allScenarios"
                    :key="scenario.uuid"
                    aria-role="listitem"
                    @click="loadScenario(scenario.uuid)"
                  >
                    <b-icon icon="crosshairs" />
                    {{ $t('viewScenario1point5.label-5') }}
                    {{ scenario.name }} ({{ scenario.timeline }})
                  </b-dropdown-item>
                  <b-dropdown-item
                    aria-role="listitem"
                    @click="createScenario()"
                  >
                    <b-icon icon="plus" />
                    {{ $t('viewScenario1point5.label-6') }}
                  </b-dropdown-item>
                  <b-dropdown-item aria-role="listitem" @click="backToAdmin()">
                    <b-icon icon="chevron-left" />
                    {{ $t('viewScenario1point5.label-8') }}
                  </b-dropdown-item>
                </b-dropdown>
              </div>
            </b-field>
            <b-menu>
              <b-menu-list :label="$t('viewScenario1point5.label-1')">
                <b-menu-item
                  @click="activate('introduction')"
                  :active="activeModule === 'introduction'"
                >
                  <template slot="label">
                    <nav class="level">
                      <div class="level-left">
                        <div class="level-item">
                          <b-icon icon="info"></b-icon>
                          {{ $t('viewScenario1point5.label-2') }}
                        </div>
                      </div>
                    </nav>
                  </template>
                </b-menu-item>
                <b-menu-item
                  v-if="authStore.isAuthenticated"
                  @click="activate('boundaries')"
                  :active="activeModule === 'boundaries'"
                  :disabled="!introduction.isValid"
                >
                  <template slot="label">
                    <nav class="level">
                      <div class="level-left">
                        <div class="level-item">
                          <b-icon icon="square"></b-icon>
                          {{ $t('viewScenario1point5.label-3') }}
                        </div>
                      </div>
                      <div v-if="authStore.isAuthenticated" class="level-right">
                        <div class="level-item">
                          <b-tag
                            rounded
                            :type="tagType(boundaries.isModified)"
                            v-show="boundaries.isValid | boundaries.isModified"
                          >
                            <b-icon icon="save"></b-icon>
                          </b-tag>
                        </div>
                      </div>
                    </nav>
                  </template>
                </b-menu-item>
                <b-menu-item
                  @click="activate('scenario-editor')"
                  :active="activeModule === 'scenario-editor'"
                  :disabled="!boundaries.isValid"
                >
                  <template slot="label">
                    <nav class="level">
                      <div class="level-left">
                        <div class="level-item">
                          <b-icon icon="crosshairs"></b-icon>
                          {{ $t('viewScenario1point5.label-4') }}
                        </div>
                      </div>
                      <div v-if="authStore.isAuthenticated" class="level-right">
                        <div class="level-item">
                          <b-tag
                            rounded
                            :type="tagType(scenarioEditor.isModified)"
                            v-show="
                              scenarioEditor.isValid | scenarioEditor.isModified
                            "
                          >
                            <b-icon icon="save"></b-icon>
                          </b-tag>
                        </div>
                      </div>
                    </nav>
                  </template>
                </b-menu-item>
                <!-- <b-menu-item
                  @click="activate('detailled-results')"
                  :active="activeModule==='detailled-results'"
                  :disabled="!boundaries.isValid"
                >
                  <template slot="label">
                    <nav class="level">
                      <div class="level-left">
                        <div class="level-item">
                          <b-icon icon="table"></b-icon>
                          {{ $t('viewScenario1point5.label-7') }}
                        </div>
                      </div>
                    </nav>
                  </template>
                </b-menu-item> -->
              </b-menu-list>
            </b-menu>
          </div>
          <div class="column">
            <introduction
              :module="introduction"
              @is-valid="introIsValid"
              v-if="activeModule === 'introduction'"
            />
            <boundaries
              v-if="activeModule === 'boundaries'"
              :module="boundaries"
              @is-valid="(val) => (boundaries.isValid = val)"
              @is-modified="(val) => (boundaries.isModified = val)"
            />
            <scenario-editor
              v-if="activeModule === 'scenario-editor' && loaded"
              :module="scenarioEditor"
              @is-valid="(val) => (scenarioEditor.isValid = val)"
              @is-modified="(val) => (scenarioEditor.isModified = val)"
            />
            <detailled-results v-if="activeModule === 'detailled-results'" />
          </div>
        </div>
      </div>
    </div>
  </section>
</template>

<script>
import carbonService from '@/services/carbonService'
import { useAdminStore } from '@/stores/admin.js'
import { useAuthenticationStore } from '@/stores/authentication.js'
import { useCoreStore } from '@/stores/core'
import { useScenarioStore } from '@/stores/scenario.js'
import { makeGHGI } from '../utils/ghgi'
import { ActiveModules } from '@/models/ActiveModules'

export default {
  name: 'Scenario1point5',
  components: {
    // 'detailled-results': () => import('@/components/scenario/DetailledResults.vue'),
    introduction: () => import('@/components/scenario/Introduction.vue'),
    boundaries: () => import('@/components/scenario/Boundaries.vue'),
    'scenario-editor': () => import('@/components/scenario/ScenarioEditor.vue')
  },
  data () {
    return {
      loaded: false,
      activeModule: 'introduction',
      introduction: {
        isValid: false
      },
      boundaries: {
        isValid: false,
        isModified: false
      },
      scenarioEditor: {
        isValid: false,
        isModified: false
      }
    }
  },
  setup () {
    const adminStore = useAdminStore()
    const authStore = useAuthenticationStore()
    const coreStore = useCoreStore()
    const scenarioStore = useScenarioStore()
    return { adminStore, authStore, coreStore, scenarioStore }
  },
  computed: {
    uuid: function () {
      return this.$route.params.uuid
    },
    scenario: function () {
      return this.scenarioStore.item
    },
    buttonLabel: function () {
      if (this.scenario.name) {
        return (
          this.$t('viewScenario1point5.label-5') +
          ' ' +
          this.scenario.name +
          ' (' +
          this.scenario.timeline +
          ')'
        )
      } else {
        return this.$t('viewScenario1point5.label-6')
      }
    },
    buttonIcon: function () {
      if (this.scenario.name) {
        return 'crosshairs'
      } else {
        return 'plus'
      }
    }
  },
  methods: {
    loadScenario: function (uuid) {
      const loadingComponent = this.$buefy.loading.open({
        container: null
      })
      this.scenarioStore
        .set(uuid)
        .then((data) => {
          loadingComponent.close()
          this.$router.push('/scenario-1point5/' + uuid)
        })
        .catch((erreur) => {
          loadingComponent.close()
        })
    },
    backToAdmin: function () {
      this.scenarioStore.setEmpty()
      this.$router.push('/administration/all-scenarios')
    },
    createScenario: function () {
      this.scenarioStore.setEmpty()
      this.$router.push('/scenario-1point5/0')
    },
    introIsValid: function () {
      this.introduction.isValid = true
      if (this.authStore.isAuthenticated) {
        this.activeModule = 'boundaries'
      } else {
        this.boundaries.isValid = true
        this.activeModule = 'scenario-editor'
      }
    },
    tagType: function (isModified) {
      let tagType = 'is-success'
      if (isModified) {
        tagType = 'is-danger'
      }
      return tagType
    },
    activate: function (module) {
      this.activeModule = module
    },
    parameterInitState: function () {
      if (this.uuid) {
        if (this.uuid === '0') {
          this.scenarioStore.setEmpty()
          this.introduction.isValid = false
          this.boundaries.isValid = false
          this.boundaries.isModified = false
          this.scenarioEditor.isValid = false
          this.scenarioEditor.isModified = false
          this.loaded = true
          this.activeModule = 'introduction'
        } else {
          this.loaded = true
          this.introduction.isValid = true
          this.boundaries.isValid = true
          this.boundaries.isModified = false
          if (this.scenario.rules.length === 0) {
            this.scenarioEditor.isValid = false
          } else {
            this.scenarioEditor.isValid = true
          }
          this.scenarioEditor.isModified = false
        }
      }
    }
  },
  watch: {
    uuid: function () {
      this.parameterInitState()
    }
  },
  mounted () {
    if (this.authStore.isAuthenticated) {
      const loadingComponent = this.$buefy.loading.open({
        container: null
      })
      this.adminStore
        .getAllGHGI()
        .then((data) => {
          if (this.uuid) {
            this.adminStore
              .getScenarios()
              .then((data) => {
                if (this.uuid === '0') {
                  this.parameterInitState()
                  loadingComponent.close()
                } else {
                  this.scenarioStore.set(this.uuid).then((data) => {
                    this.parameterInitState()
                    loadingComponent.close()
                  })
                  this.activeModule = 'scenario-editor'
                  loadingComponent.close()
                }
              })
              .catch((erreur) => {
                loadingComponent.close()
              })
          } else {
            this.scenarioStore.setEmpty()
            this.loaded = true
            loadingComponent.close()
          }
        })
        .catch((erreur) => {
          loadingComponent.close()
        })
    } else {
      const loadingComponent = this.$buefy.loading.open({
        container: null
      })

      // Let load fake data into the whole app
      this.scenarioStore.setEmpty()
      carbonService.getFakeGHGI().then((fakeGHGI) => {
        // the fakeGHGI retrieved must fit with the configuration (labels, members, travel purposes/position)
        this.adminStore.setGHGI(fakeGHGI)
        this.scenarioStore.setGHGI(
          makeGHGI(
            fakeGHGI,
            ActiveModules.fromSettings(this.coreStore.settings),
            this.coreStore.surveyLabelMapping
          )
        )
        this.loaded = true
        loadingComponent.close()
      })
    }
  }
}
</script>
